import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, Users, Calendar, Clock,
  Plus, Edit, Trash2, Euro, Save, Image, Palette, Type, Tag, UserPlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth, api } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { pt, enGB } from 'date-fns/locale';

const AdminPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { refetch: refetchGlobalSettings } = useSiteSettings();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [bookings, setBookings] = useState([]);
  // Estados para os filtros da aba de marcações \/
const [filterDate, setFilterDate] = useState("");
const [filterService, setFilterService] = useState("all");
const [filterStatus, setFilterStatus] = useState("all");
const [filterPayment, setFilterPayment] = useState("all");
// feito pelo gemini /\
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros para Ausências (blocked slots)
  const [blockFilterDate, setBlockFilterDate] = useState('');
  const [blockFilterReason, setBlockFilterReason] = useState('all');
  const [editingBlock, setEditingBlock] = useState(null);

  // Dialogs
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState('all');
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [guestBookingDialogOpen, setGuestBookingDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  // Forms
  const [serviceForm, setServiceForm] = useState({
    name_pt: '', name_en: '', description_pt: '', description_en: '',
    price: '', duration: '', buffer_time: '15', loyalty_points: '', category: 'facial', images: [''], image_fit: 'cover', staff_ids: []
  });

  const [staffForm, setStaffForm] = useState({
    name: '', email: '', phone: '', bio_pt: '', bio_en: '', specialties: [], photo: ''
  });

  const [blockForm, setBlockForm] = useState({
    start_time: '', end_time: '', reason: 'lunch', staff_id: ''
  });

  const [categoryForm, setCategoryForm] = useState({
    slug: '', name_pt: '', name_en: '', order: 0
  });

  const [guestBookingForm, setGuestBookingForm] = useState({
    guest_name: '', guest_email: '', guest_phone: '', service_id: '', staff_id: '', start_time: '', notes: ''
  });

  //feito por gemini \/
  const filteredBookings = bookings.filter(booking => {
  // Filtro por Data
  const matchesDate = !filterDate || (booking.start_time && booking.start_time.includes(filterDate));
  // Filtro por Serviço
  const matchesService = filterService === "all" || booking.service_id === filterService;
  // Filtro por Status
  const matchesStatus = filterStatus === "all" || booking.status === filterStatus;
  // Filtro por Pagamento
  const matchesPayment = filterPayment === "all" || 
    (filterPayment === "paid" ? booking.payment_status === "paid" : booking.payment_status !== "paid");

  return matchesDate && matchesService && matchesStatus && matchesPayment;
});
const otherBookings = bookings.filter(b => !filteredBookings.includes(b));
  const isFiltering = filterDate !== "" || filterService !== "all" || filterStatus !== "all" || filterPayment !== "all";

  // Filtrar bloqueios
  const filteredBlockedSlots = blockedSlots.filter(block => {
    const matchesDate = !blockFilterDate || (block.start_time && block.start_time.includes(blockFilterDate));
    const matchesReason = blockFilterReason === 'all' || block.reason === blockFilterReason;
    return matchesDate && matchesReason;
  });

  const isFilteringBlocks = blockFilterDate !== '' || blockFilterReason !== 'all';
//feito por gemini /\

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'staff') {
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [user, navigate]);

  const fetchAllData = async () => {
    try {
      const [dashboardRes, servicesRes, staffRes, bookingsRes, blocksRes, settingsRes, categoriesRes] = await Promise.all([
        api.get('/admin/dashboard').catch(() => ({ data: null })),
        api.get('/services?active_only=false'),
        api.get('/staff?active_only=false'),
        api.get('/admin/bookings').catch(() => ({ data: [] })),
        api.get('/admin/blocked-slots').catch(() => ({ data: [] })),
        api.get('/settings'),
        api.get('/categories?active_only=false')
      ]);
      setDashboardData(dashboardRes.data);
      setServices(servicesRes.data);
      setStaff(staffRes.data);
      setBookings(bookingsRes.data);
      setBlockedSlots(blocksRes.data);
      setSiteSettings(settingsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Service handlers
const handleSaveService = async () => {
  try {
    // Criamos o objeto de dados garantindo que os campos numéricos sejam convertidos
    const data = {
      ...serviceForm,
      price: parseFloat(serviceForm.price),
      duration: parseInt(serviceForm.duration),
      buffer_time: parseInt(serviceForm.buffer_time || 0),
      loyalty_points: parseInt(serviceForm.loyalty_points || 0),
      staff_ids: serviceForm.staff_ids || [],
      images: (serviceForm.images || []).map(s => (s || '').trim()).filter(Boolean)
    };

    console.log("Enviando dados para o servidor:", data); // Debug para ver se os pontos aparecem no console

    if (editingService) {
      // Note que a rota admin costuma ser /admin/services, verifique se a sua não precisa do prefixo
      await api.put(`/services/${editingService.service_id}`, data);
      toast.success('Serviço atualizado com sucesso');
    } else {
      await api.post('/services', data);
      toast.success('Serviço criado com sucesso');
    }

    setServiceDialogOpen(false);
    resetServiceForm();
    // Refresh só serviços (sem scroll ao topo)
    const res = await api.get('/services');
    setServices(res.data);
  } catch (error) {
    console.error('Erro ao guardar serviço:', error);
    toast.error('Erro ao guardar serviço');
  }
};

  const handleDeleteService = async (serviceId) => {
    if (!confirm('Tem certeza que deseja eliminar este serviço?')) return;
    try {
      await api.delete(`/services/${serviceId}`);
      toast.success('Serviço eliminado');
      const res = await api.get('/services');
      setServices(res.data);
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const resetServiceForm = () => {
    setServiceForm({ name_pt: '', name_en: '', description_pt: '', description_en: '', price: '', duration: '', buffer_time: '15', loyalty_points: '', category: 'facial', images: [''], image_fit: 'cover', staff_ids: [] });
    setEditingService(null);
  };

  // Staff handlers
  const handleSaveStaff = async () => {
    try {
      if (editingStaff) {
        await api.put(`/staff/${editingStaff.staff_id}`, staffForm);
        toast.success('Colaborador atualizado');
      } else {
        await api.post('/staff', staffForm);
        toast.success('Colaborador criado');
      }
      setStaffDialogOpen(false);
      resetStaffForm();
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao guardar');
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!confirm('Tem certeza?')) return;
    try {
      await api.delete(`/staff/${staffId}`);
      toast.success('Colaborador eliminado');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const resetStaffForm = () => {
    setStaffForm({ name: '', email: '', phone: '', bio_pt: '', bio_en: '', specialties: [], photo: '' });
    setEditingStaff(null);
  };

  // Block handlers
  const handleSaveBlock = async () => {
    try {
      if (editingBlock) {
        // Update existing block
        await api.put(`/admin/blocked-slots/${editingBlock.block_id}`, { 
          ...blockForm, 
          staff_id: blockForm.staff_id || null 
        });
        toast.success('Bloqueio atualizado');
        setEditingBlock(null);
      } else {
        // Create new block
        await api.post('/admin/blocked-slots', { ...blockForm, staff_id: blockForm.staff_id || null });
        toast.success('Horário bloqueado');
      }
      setBlockDialogOpen(false);
      setBlockForm({ start_time: '', end_time: '', reason: 'lunch', staff_id: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao guardar bloqueio');
    }
  };

  const handleEditBlock = (block) => {
    setEditingBlock(block);
    setBlockForm({
      start_time: block.start_time ? format(new Date(block.start_time), "yyyy-MM-dd'T'HH:mm") : '',
      end_time: block.end_time ? format(new Date(block.end_time), "yyyy-MM-dd'T'HH:mm") : '',
      reason: block.reason || 'lunch',
      staff_id: block.staff_id || ''
    });
    setBlockDialogOpen(true);
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Eliminar este bloqueio?')) return;
    try {
      await api.delete(`/admin/blocked-slots/${blockId}`);
      toast.success('Bloqueio removido');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  // Settings handlers
  const handleSaveSettings = async () => {
    try {
      await api.put('/settings', siteSettings);
      toast.success('Configurações guardadas! As alterações já estão visíveis no site.');
      // Atualizar as settings globais para que todas as páginas vejam as alterações
      await refetchGlobalSettings();
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao guardar configurações');
    }
  };

  // Booking handlers
  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await api.put(`/admin/bookings/${bookingId}/status`, { status });
      toast.success('Estado atualizado');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleConfirmPayment = async (bookingId) => {
    try {
      await api.put(`/admin/bookings/${bookingId}/confirm-payment`);
      toast.success('Pagamento confirmado! Pontos de fidelidade creditados.');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar pagamento');
    }
  };

  // Category handlers
  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.category_id}`, categoryForm);
        toast.success('Categoria atualizada');
      } else {
        await api.post('/categories', categoryForm);
        toast.success('Categoria criada');
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar categoria');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Tem certeza que deseja eliminar esta categoria?')) return;
    try {
      await api.delete(`/categories/${categoryId}`);
      toast.success('Categoria eliminada');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar categoria');
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({ slug: '', name_pt: '', name_en: '', order: 0 });
    setEditingCategory(null);
  };

  // Guest booking handlers
  const handleCreateGuestBooking = async () => {
    try {
      if (!guestBookingForm.guest_name || !guestBookingForm.guest_email || !guestBookingForm.guest_phone || !guestBookingForm.service_id || !guestBookingForm.start_time) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
      
      await api.post('/bookings/guest', guestBookingForm);
      toast.success('Agendamento criado! O cliente receberá as notificações.');
      setGuestBookingDialogOpen(false);
      resetGuestBookingForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar agendamento');
    }
  };

  const resetGuestBookingForm = () => {
    setGuestBookingForm({ guest_name: '', guest_email: '', guest_phone: '', service_id: '', staff_id: '', start_time: '', notes: '' });
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: language === 'pt' ? pt : enGB });
  };

  const getStatusBadge = (status) => {
    const colors = { pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-green-100 text-green-800', completed: 'bg-blue-100 text-blue-800', cancelled: 'bg-red-100 text-red-800' };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-24 flex items-center justify-center">
        <div className="animate-pulse text-[#BFA05F]">A carregar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-24 pb-16" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#1A1815]">Administração</h1>
          <p className="text-[#635E54] mt-1">Bem-vinda, {user?.name}!</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-[#E6E2DA] rounded-full p-1 mb-8 flex-wrap h-auto gap-1">
            <TabsTrigger value="dashboard" className="rounded-full data-[state=active]:bg-[#1A1815] data-[state=active]:text-white">
              <LayoutDashboard className="w-4 h-4 mr-2" />Painel
            </TabsTrigger>
            <TabsTrigger value="services" className="rounded-full data-[state=active]:bg-[#1A1815] data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" />Serviços
            </TabsTrigger>
            <TabsTrigger value="categories" className="rounded-full data-[state=active]:bg-[#1A1815] data-[state=active]:text-white">
              <Tag className="w-4 h-4 mr-2" />Categorias
            </TabsTrigger>
            <TabsTrigger value="team" className="rounded-full data-[state=active]:bg-[#1A1815] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />Equipa
            </TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-full data-[state=active]:bg-[#1A1815] data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />Marcações
            </TabsTrigger>
            <TabsTrigger value="schedule" className="rounded-full data-[state=active]:bg-[#1A1815] data-[state=active]:text-white">
              <Clock className="w-4 h-4 mr-2" />Ausências
            </TabsTrigger>
            <TabsTrigger value="design" className="rounded-full data-[state=active]:bg-[#1A1815] data-[state=active]:text-white">
              <Palette className="w-4 h-4 mr-2" />Design
            </TabsTrigger>
            <TabsTrigger value="content" className="rounded-full data-[state=active]:bg-[#1A1815] data-[state=active]:text-white">
              <Type className="w-4 h-4 mr-2" />Textos
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#BFA05F]/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-[#BFA05F]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#635E54]">Marcações Hoje</p>
                    <p className="text-2xl font-semibold text-[#1A1815]">{dashboardData?.today_bookings || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <Euro className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#635E54]">Receita Hoje</p>
                    <p className="text-2xl font-semibold text-[#1A1815]">€{dashboardData?.today_revenue || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#635E54]">Total Clientes</p>
                    <p className="text-2xl font-semibold text-[#1A1815]">{dashboardData?.total_clients || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#635E54]">Pendentes</p>
                    <p className="text-2xl font-semibold text-[#1A1815]">{dashboardData?.pending_bookings || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Bookings */}
            <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
              <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4">Marcações Recentes</h3>
              <div className="space-y-3">
                {bookings.length === 0 ? (
                  <p className="text-sm text-[#A8A29A] text-center py-4">Nenhuma marcação encontrada</p>
                ) : (
                  [...bookings].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5).map((booking) => (
                    <div key={booking.booking_id} className="flex items-center justify-between p-3 rounded-xl bg-[#F9F8F6]">
                      <div>
                        <p className="font-medium text-[#1A1815]">{booking.user?.name}</p>
                        <p className="text-sm text-[#635E54]">{booking.service?.name_pt}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-[#635E54]">{formatDateTime(booking.start_time)}</p>
                        <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Services Tab */}
<TabsContent value="services" className="space-y-6">
  <div className="flex flex-wrap justify-between items-center gap-3">
    <h2 className="font-heading text-xl font-medium text-[#1A1815]">Gestão de Serviços</h2>
    <div className="flex items-center gap-3">
      <select
        value={serviceCategoryFilter}
        onChange={(e) => setServiceCategoryFilter(e.target.value)}
        className="px-3 py-2 rounded-full border border-[#E6E2DA] bg-white text-sm text-[#1A1815] focus:border-[#BFA05F] outline-none"
      >
        <option value="all">Todas as categorias</option>
        {categories.filter(c => c.is_active).map(cat => (
          <option key={cat.category_id} value={cat.slug}>{cat.name_pt}</option>
        ))}
      </select>
      <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
      <DialogTrigger asChild>
        <Button onClick={resetServiceForm} className="bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full">
          <Plus className="w-4 h-4 mr-2" />Adicionar Serviço
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{editingService ? 'Editar Serviço' : 'Novo Serviço'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#635E54]">Nome (PT) *</label>
              <Input value={serviceForm.name_pt} onChange={(e) => setServiceForm({ ...serviceForm, name_pt: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-sm text-[#635E54]">Name (EN) *</label>
              <Input value={serviceForm.name_en} onChange={(e) => setServiceForm({ ...serviceForm, name_en: e.target.value })} className="mt-1" />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#635E54]">Descrição (PT)</label>
              <Textarea value={serviceForm.description_pt} onChange={(e) => setServiceForm({ ...serviceForm, description_pt: e.target.value })} className="mt-1" rows={3} />
            </div>
            <div>
              <label className="text-sm text-[#635E54]">Description (EN)</label>
              <Textarea value={serviceForm.description_en} onChange={(e) => setServiceForm({ ...serviceForm, description_en: e.target.value })} className="mt-1" rows={3} />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-[#635E54]">Preço (€) *</label>
              <Input 
                type="number" 
                value={serviceForm.price} 
                onChange={(e) => {
                  const val = e.target.value;
                  // Sugestão automática: 10% do preço como pontos
                  const suggestedPoints = val ? Math.floor(parseFloat(val) * 0.1) : 0;
                  setServiceForm({ ...serviceForm, price: val, loyalty_points: suggestedPoints.toString() });
                }} 
                className="mt-1" 
              />
            </div>
            <div>
              <label className="text-sm text-[#635E54]">Duração (min) *</label>
              <Input type="number" value={serviceForm.duration} onChange={(e) => setServiceForm({ ...serviceForm, duration: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-sm text-[#635E54]">Intervalo (min)</label>
              <Input type="number" value={serviceForm.buffer_time} onChange={(e) => setServiceForm({ ...serviceForm, buffer_time: e.target.value })} className="mt-1" />
            </div>
            {/* NOVO CAMPO: PONTOS DE FIDELIDADE */}
            <div>
              <label className="text-sm font-medium text-blue-600">Pontos Ganhos</label>
              <Input 
                type="number" 
                value={serviceForm.loyalty_points} 
                onChange={(e) => setServiceForm({ ...serviceForm, loyalty_points: e.target.value })} 
                className="mt-1 border-blue-200 focus:border-blue-400"
                placeholder="Ex: 5"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-[#635E54]">Categoria</label>
            <Select value={serviceForm.category} onValueChange={(value) => setServiceForm({ ...serviceForm, category: value })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.filter(c => c.is_active).map((cat) => (
                  <SelectItem key={cat.category_id} value={cat.slug}>
                    {language === 'pt' ? cat.name_pt : cat.name_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div data-testid="service-images-section">
            <label className="text-sm text-[#635E54] font-medium">Fotos do Serviço</label>
            <p className="text-xs text-[#A8A29A] mb-2">Adicione uma ou mais fotos. Com 2 ou mais fotos, o card mostra um carrossel automático.</p>
            <div className="space-y-2">
              {(serviceForm.images && serviceForm.images.length > 0 ? serviceForm.images : ['']).map((img, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    data-testid={`service-image-url-${idx}`}
                    value={img}
                    onChange={(e) => {
                      const base = (serviceForm.images && serviceForm.images.length > 0) ? [...serviceForm.images] : [''];
                      base[idx] = e.target.value;
                      setServiceForm({ ...serviceForm, images: base });
                    }}
                    placeholder="https://..."
                    className="flex-1"
                  />
                  {img ? (
                    <img src={img} alt="" className="w-10 h-10 rounded-lg object-cover border border-[#E6E2DA] shrink-0" onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    data-testid={`remove-service-image-${idx}`}
                    onClick={() => {
                      const src = (serviceForm.images && serviceForm.images.length > 0) ? serviceForm.images : [''];
                      setServiceForm({ ...serviceForm, images: src.filter((_, i) => i !== idx) });
                    }}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="add-service-image-btn"
              onClick={() => {
                const base = (serviceForm.images && serviceForm.images.length > 0) ? serviceForm.images : [''];
                setServiceForm({ ...serviceForm, images: [...base, ''] });
              }}
              className="mt-2 rounded-full border-[#BFA05F] text-[#BFA05F] hover:bg-[#BFA05F]/5"
            >
              <Plus className="w-4 h-4 mr-1" />Adicionar foto
            </Button>

            <label className="flex items-center gap-2 mt-3 p-2 rounded-lg border border-[#E6E2DA] cursor-pointer hover:border-[#BFA05F]/50 transition-colors" data-testid="image-fit-toggle">
              <input
                type="checkbox"
                checked={serviceForm.image_fit === 'contain'}
                onChange={(e) => setServiceForm({ ...serviceForm, image_fit: e.target.checked ? 'contain' : 'cover' })}
                className="accent-[#BFA05F] w-4 h-4"
                data-testid="image-fit-checkbox"
              />
              <span className="text-sm text-[#1A1815]">Ajustar imagem inteira ao card (sem cortar)</span>
            </label>
          </div>

          {/* REGRA 1: Selecionar colaboradores que fazem este serviço */}
          <div>
            <label className="text-sm text-[#635E54] font-medium">Colaboradores que realizam este serviço</label>
            <p className="text-xs text-[#A8A29A] mb-2">Deixe todos desmarcados para aceitar qualquer profissional</p>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {staff.filter(s => s.is_active).map(s => {
                const isChecked = (serviceForm.staff_ids || []).includes(s.staff_id);
                return (
                  <label key={s.staff_id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${isChecked ? 'border-[#BFA05F] bg-[#BFA05F]/5' : 'border-[#E6E2DA] hover:border-[#BFA05F]/50'}`}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {
                        const current = serviceForm.staff_ids || [];
                        const updated = isChecked ? current.filter(id => id !== s.staff_id) : [...current, s.staff_id];
                        setServiceForm({ ...serviceForm, staff_ids: updated });
                      }}
                      className="accent-[#BFA05F] w-4 h-4"
                    />
                    <span className="text-sm text-[#1A1815]">{s.name}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <Button onClick={handleSaveService} className="w-full bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full">
            <Save className="w-4 h-4 mr-2" />Guardar Serviço
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </div>
  </div>

  <div className="grid gap-4">
    {services.filter(s => serviceCategoryFilter === 'all' || s.category === serviceCategoryFilter).map((service) => (
      <div key={service.service_id} className="bg-white rounded-2xl p-6 border border-[#E6E2DA] flex items-center justify-between">
        <div className="flex items-center gap-4">
          {service.images?.[0] ? (
            <img src={service.images[0]} alt="" className="w-20 h-20 rounded-xl object-cover" onError={(e) => { e.target.src = 'https://images.pexels.com/photos/5659012/pexels-photo-5659012.jpeg'; }} />
          ) : (
            <div className="w-20 h-20 rounded-xl bg-[#F2F0EB] flex items-center justify-center">
              <Image className="w-8 h-8 text-[#A8A29A]" />
            </div>
          )}
          <div>
            <h3 className="font-medium text-[#1A1815]">{service.name_pt}</h3>
            <div className="flex items-center gap-3 mt-2">
              <Badge variant="outline">{service.category}</Badge>
              <span className="text-[#BFA05F] font-semibold">€{service.price}</span>
              <span className="text-sm text-[#635E54]">{service.duration}min</span>
              {/* DISPLAY DOS PONTOS NA LISTA */}
              <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full font-medium">
                ★ {service.loyalty_points || 0} pts
              </span>
              {service.staff_ids && service.staff_ids.length > 0 && (
                <span className="text-xs text-[#635E54]">
                  {service.staff_ids.map(id => staff.find(s => s.staff_id === id)?.name).filter(Boolean).join(', ')}
                </span>
              )}
              {(!service.staff_ids || service.staff_ids.length === 0) && (
                <span className="text-xs text-[#A8A29A] italic">Todos os profissionais</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { 
            setEditingService(service); 
            setServiceForm({ 
              ...service, 
              price: service.price.toString(), 
              duration: service.duration.toString(), 
              buffer_time: service.buffer_time.toString(),
              loyalty_points: (service.loyalty_points || 0).toString(),
              images: (service.images && service.images.length > 0) ? service.images : [''],
              image_fit: service.image_fit || 'cover',
              staff_ids: service.staff_ids || []
            }); 
            setServiceDialogOpen(true); 
          }} className="rounded-full">
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleDeleteService(service.service_id)} className="rounded-full text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    ))}
  </div>
</TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl font-medium text-[#1A1815]">Gestão de Categorias</h2>
              <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetCategoryForm} className="bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full">
                    <Plus className="w-4 h-4 mr-2" />Nova Categoria
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-heading">{editingCategory ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="text-sm text-[#635E54]">Identificador (slug) *</label>
                      <Input 
                        value={categoryForm.slug} 
                        onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') })} 
                        placeholder="Ex: depilacao, massagem_relaxante"
                        className="mt-1" 
                        disabled={editingCategory}
                      />
                      <p className="text-xs text-[#A8A29A] mt-1">Usado internamente. Não pode ser alterado depois.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-[#635E54]">Nome (PT) *</label>
                        <Input value={categoryForm.name_pt} onChange={(e) => setCategoryForm({ ...categoryForm, name_pt: e.target.value })} className="mt-1" />
                      </div>
                      <div>
                        <label className="text-sm text-[#635E54]">Name (EN)</label>
                        <Input value={categoryForm.name_en} onChange={(e) => setCategoryForm({ ...categoryForm, name_en: e.target.value })} className="mt-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Ordem</label>
                      <Input type="number" value={categoryForm.order} onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })} className="mt-1" />
                    </div>
                    <Button onClick={handleSaveCategory} className="w-full bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full">
                      <Save className="w-4 h-4 mr-2" />Guardar Categoria
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {categories.map((category) => (
                <div key={category.category_id} className={`bg-white rounded-2xl p-6 border ${category.is_active ? 'border-[#E6E2DA]' : 'border-red-200 bg-red-50/50'} flex items-center justify-between`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#BFA05F]/10 flex items-center justify-center">
                      <Tag className="w-6 h-6 text-[#BFA05F]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#1A1815]">{category.name_pt}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-[#635E54]">slug: {category.slug}</span>
                        {!category.is_active && <Badge variant="outline" className="text-red-600 border-red-300">Inativa</Badge>}
                        <span className="text-sm text-[#A8A29A]">
                          {services.filter(s => s.category === category.slug).length} serviços
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { 
                      setEditingCategory(category); 
                      setCategoryForm({ slug: category.slug, name_pt: category.name_pt, name_en: category.name_en || '', order: category.order || 0 }); 
                      setCategoryDialogOpen(true); 
                    }} className="rounded-full">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteCategory(category.category_id)} className="rounded-full text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl font-medium text-[#1A1815]">Gestão de Equipa</h2>
              <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetStaffForm} className="bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full">
                    <Plus className="w-4 h-4 mr-2" />Adicionar Colaborador
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-heading">{editingStaff ? 'Editar Colaborador' : 'Novo Colaborador'}</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    {/* Photo URL */}
                    <div>
                      <label className="text-sm text-[#635E54]">URL da Foto</label>
                      <Input 
                        value={staffForm.photo || ''} 
                        onChange={(e) => setStaffForm({ ...staffForm, photo: e.target.value })} 
                        placeholder="https://res.cloudinary.com/... ou https://images.unsplash.com/..." 
                        className="mt-1" 
                      />
                      {staffForm.photo && (
                        <div className="mt-2 flex justify-center">
                          <img 
                            src={staffForm.photo} 
                            alt="Preview" 
                            className="w-24 h-24 rounded-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Nome *</label>
                      <Input value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Email *</label>
                      <Input type="email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Telefone/WhatsApp</label>
                      <Input value={staffForm.phone || ''} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} placeholder="+351 912 345 678" className="mt-1" />
                      <p className="text-xs text-[#A8A29A] mt-1">Para receber notificações de agendamentos</p>
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Função/Especialidades</label>
                      <Input 
                        value={staffForm.specialties?.join(', ') || ''} 
                        onChange={(e) => setStaffForm({ ...staffForm, specialties: e.target.value.split(',').map(s => s.trim()).filter(s => s) })} 
                        placeholder="Ex: Esteticista, Massagista"
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Bio (PT)</label>
                      <Textarea value={staffForm.bio_pt} onChange={(e) => setStaffForm({ ...staffForm, bio_pt: e.target.value })} className="mt-1" />
                    </div>
                    <Button onClick={handleSaveStaff} className="w-full bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full">
                      <Save className="w-4 h-4 mr-2" />Guardar
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {staff.map((member) => (
                <div key={member.staff_id} className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                  <div className="flex items-center gap-4 mb-4">
                    {member.photo ? (
                      <img 
                        src={member.photo} 
                        alt={member.name} 
                        className="w-16 h-16 rounded-full object-cover" 
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-[#F2F0EB] flex items-center justify-center">
                        <Users className="w-8 h-8 text-[#A8A29A]" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-[#1A1815]">{member.name}</h3>
                      <p className="text-sm text-[#635E54]">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditingStaff(member); setStaffForm({ name: member.name, email: member.email, phone: member.phone || '', bio_pt: member.bio_pt || '', bio_en: member.bio_en || '', specialties: member.specialties || [], photo: member.photo || '' }); setStaffDialogOpen(true); }} className="flex-1 rounded-full">
                      <Edit className="w-4 h-4 mr-1" /> Editar
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteStaff(member.staff_id)} className="rounded-full text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-6">
  {/* CABEÇALHO */}
  <div className="flex justify-between items-center">
    <h2 className="font-heading text-xl font-medium text-[#1A1815]">Gestão de Marcações</h2>
    <Dialog open={guestBookingDialogOpen} onOpenChange={setGuestBookingDialogOpen}>
      <DialogTrigger asChild>
        <Button onClick={resetGuestBookingForm} className="bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full">
          <UserPlus className="w-4 h-4 mr-2" />Agendar para Cliente
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Agendar para Cliente</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm text-[#635E54]">Nome do Cliente *</label>
            <Input value={guestBookingForm.guest_name} onChange={(e) => setGuestBookingForm({ ...guestBookingForm, guest_name: e.target.value })} placeholder="Nome completo" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input type="email" value={guestBookingForm.guest_email} onChange={(e) => setGuestBookingForm({ ...guestBookingForm, guest_email: e.target.value })} placeholder="Email" />
            <Input value={guestBookingForm.guest_phone} onChange={(e) => setGuestBookingForm({ ...guestBookingForm, guest_phone: e.target.value })} placeholder="WhatsApp" />
          </div>
          <Select value={guestBookingForm.service_id} onValueChange={(v) => setGuestBookingForm({ ...guestBookingForm, service_id: v })}>
            <SelectTrigger><SelectValue placeholder="Selecione o serviço" /></SelectTrigger>
            <SelectContent>
              {services.filter(s => s.is_active).map(s => (
                <SelectItem key={s.service_id} value={s.service_id}>{s.name_pt} - €{s.price}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="datetime-local" value={guestBookingForm.start_time} onChange={(e) => setGuestBookingForm({ ...guestBookingForm, start_time: e.target.value })} />
          <Button onClick={handleCreateGuestBooking} className="w-full bg-[#BFA05F] text-white rounded-full">Criar Agendamento</Button>
        </div>
      </DialogContent>
    </Dialog>
  </div>

  {/* BARRA DE FILTROS */}
  <div className="space-y-2">
    <div className="bg-[#F9F8F6] p-4 rounded-2xl border border-[#E6E2DA] grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="space-y-1">
        <label className="text-[10px] font-bold text-[#635E54] uppercase ml-1">Data</label>
        <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="bg-white h-9" />
      </div>
      <div className="space-y-1">
        <label className="text-[10px] font-bold text-[#635E54] uppercase ml-1">Serviço</label>
        <Select value={filterService} onValueChange={setFilterService}>
          <SelectTrigger className="bg-white h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Serviços</SelectItem>
            {services.map(s => <SelectItem key={s.service_id} value={s.service_id}>{s.name_pt}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <label className="text-[10px] font-bold text-[#635E54] uppercase ml-1">Status</label>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="bg-white h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Status</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
            <SelectItem value="confirmed">Confirmado</SelectItem>
            <SelectItem value="completed">Concluído</SelectItem>
            <SelectItem value="cancelled">Cancelado</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <label className="text-[10px] font-bold text-[#635E54] uppercase ml-1">Pagamento</label>
        <Select value={filterPayment} onValueChange={setFilterPayment}>
          <SelectTrigger className="bg-white h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="paid">Pago</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    {/* BOTÃO LIMPAR FILTROS (Só aparece se algo estiver filtrado) */}
    {(filterDate !== "" || filterService !== "all" || filterStatus !== "all" || filterPayment !== "all") && (
      <div className="flex justify-end">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => { setFilterDate(""); setFilterService("all"); setFilterStatus("all"); setFilterPayment("all"); }}
          className="text-[#BFA05F] hover:text-[#9E8246] hover:bg-[#BFA05F]/10 text-xs h-7"
        >
          Limpar Filtros
        </Button>
      </div>
    )}
  </div>

  {/* LISTAGEM PRINCIPAL (RESULTADOS DO FILTRO) */}
  <div className="space-y-4">
    {filteredBookings.length > 0 ? (
      filteredBookings.map((booking) => (
        <div key={booking.booking_id} className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-[#BFA05F]/10 flex items-center justify-center text-[#BFA05F] font-semibold">
                {booking.user?.name?.charAt(0) || booking.guest_name?.charAt(0) || '?'}
              </div>
              <div>
                <h3 className="font-medium text-[#1A1815]">{booking.user?.name || booking.guest_name}</h3>
                <p className="text-sm text-[#BFA05F]">{booking.service?.name_pt}</p>
                <p className="text-sm text-[#635E54]">{formatDateTime(booking.start_time)}</p>
                <div className="flex gap-2 mt-2">
                  <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                  <Badge className={booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                    {booking.payment_status === 'paid' ? 'Pago' : 'Pendente'}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
  {booking.status === 'pending' && (
    <Button 
      onClick={() => handleUpdateBookingStatus(booking.booking_id, 'confirmed')} 
      className="bg-green-600 text-white rounded-full" 
      size="sm"
    >
      Confirmar
    </Button>
  )}

  {booking.status === 'confirmed' && (
    <Button 
      onClick={async () => {
        try {
          // Se não estiver pago, confirma o pagamento primeiro
          if (booking.payment_status !== 'paid') {
            await handleConfirmPayment(booking.booking_id);
          }
          // Depois marca como concluído
          await handleUpdateBookingStatus(booking.booking_id, 'completed');
        } catch (err) {
          console.error(err);
        }
      }} 
      className="bg-blue-600 text-white rounded-full" 
      size="sm"
    >
      Concluir
    </Button>
  )}

  {booking.status !== 'cancelled' && booking.status !== 'completed' && (
    <Button 
      onClick={() => handleUpdateBookingStatus(booking.booking_id, 'cancelled')} 
      variant="outline" 
      className="text-red-600 border-red-200 rounded-full" 
      size="sm"
    >
      Cancelar
    </Button>
  )}
</div>
          </div>
        </div>
      ))
    ) : (
      <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-[#E6E2DA]">
        <p className="text-[#635E54] text-sm">Nenhuma marcação corresponde aos filtros.</p>
      </div>
    )}

    {/* SEÇÃO DEMAIS MARCAÇÕES */}
    {(filterDate !== "" || filterService !== "all" || filterStatus !== "all" || filterPayment !== "all") && (
      <div className="pt-8">
        <div className="relative mb-8">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E6E2DA]"></div></div>
          <div className="relative flex justify-center">
            <span className="bg-[#F9F8F6] px-4 text-xs font-bold text-[#635E54] uppercase tracking-widest">Demais Marcações</span>
          </div>
        </div>
        <div className="space-y-4 opacity-50 pointer-events-none grayscale-[50%]">
          {bookings.filter(b => !filteredBookings.includes(b)).map((booking) => (
            <div key={`other-${booking.booking_id}`} className="bg-white/50 rounded-2xl p-6 border border-[#E6E2DA]">
               <div className="flex justify-between items-center">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 font-semibold">
                      {booking.user?.name?.charAt(0) || booking.guest_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-500">{booking.user?.name || booking.guest_name}</h3>
                      <p className="text-sm text-gray-400">{booking.service?.name_pt} • {formatDateTime(booking.start_time)}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-gray-400 border-gray-200">{booking.status}</Badge>
               </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
</TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <h2 className="font-heading text-xl font-medium text-[#1A1815]">Ausências adicionadas</h2>
              <Dialog open={blockDialogOpen} onOpenChange={(open) => {
                setBlockDialogOpen(open);
                if (!open) {
                  setEditingBlock(null);
                  setBlockForm({ start_time: '', end_time: '', reason: 'lunch', staff_id: '' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full">
                    <Plus className="w-4 h-4 mr-2" />Adicionar Ausências
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-heading">
                      {editingBlock ? 'Editar Bloqueio' : 'Bloquear Horário'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="text-sm text-[#635E54]">Início</label>
                      <Input type="datetime-local" value={blockForm.start_time} onChange={(e) => setBlockForm({ ...blockForm, start_time: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Fim</label>
                      <Input type="datetime-local" value={blockForm.end_time} onChange={(e) => setBlockForm({ ...blockForm, end_time: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Motivo</label>
                      <Select value={blockForm.reason} onValueChange={(value) => setBlockForm({ ...blockForm, reason: value })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lunch">Almoço</SelectItem>
                          <SelectItem value="break">Pausa</SelectItem>
                          <SelectItem value="absence">Ausência</SelectItem>
                          <SelectItem value="holiday">Férias</SelectItem>
                          <SelectItem value="meeting">Reunião</SelectItem>
                          <SelectItem value="other">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSaveBlock} className="flex-1 bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full">
                        <Save className="w-4 h-4 mr-2" />{editingBlock ? 'Atualizar' : 'Guardar'}
                      </Button>
                      {editingBlock && (
                        <Button 
                          variant="outline" 
                          onClick={() => handleDeleteBlock(editingBlock.block_id)} 
                          className="rounded-full text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Filtros de Ausências */}
            <div className="bg-white rounded-2xl p-4 border border-[#E6E2DA]">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm text-[#635E54]">Filtrar por Data</label>
                  <Input 
                    type="date" 
                    value={blockFilterDate} 
                    onChange={(e) => setBlockFilterDate(e.target.value)} 
                    className="mt-1" 
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm text-[#635E54]">Filtrar por Tipo</label>
                  <Select value={blockFilterReason} onValueChange={setBlockFilterReason}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os tipos</SelectItem>
                      <SelectItem value="lunch">Almoço</SelectItem>
                      <SelectItem value="break">Pausa</SelectItem>
                      <SelectItem value="absence">Ausência</SelectItem>
                      <SelectItem value="holiday">Férias</SelectItem>
                      <SelectItem value="meeting">Reunião</SelectItem>
                      <SelectItem value="other">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isFilteringBlocks && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setBlockFilterDate(''); setBlockFilterReason('all'); }}
                    className="rounded-full"
                  >
                    Limpar filtros
                  </Button>
                )}
              </div>
              {isFilteringBlocks && (
                <p className="text-sm text-[#635E54] mt-2">
                  A mostrar {filteredBlockedSlots.length} de {blockedSlots.length} bloqueios
                </p>
              )}
            </div>

            <div className="space-y-4">
              {filteredBlockedSlots.length === 0 ? (
                <div className="bg-white rounded-2xl p-10 text-center border border-[#E6E2DA]">
                  <Clock className="w-12 h-12 text-[#D1CCC4] mx-auto mb-4" />
                  <p className="text-[#635E54]">
                    {isFilteringBlocks ? 'Nenhum bloqueio encontrado com esses filtros' : 'Nenhum horário bloqueado'}
                  </p>
                </div>
              ) : filteredBlockedSlots.map((block) => (
                <div key={block.block_id} className="bg-white rounded-2xl p-6 border border-[#E6E2DA] flex items-center justify-between hover:shadow-md transition-shadow">
                  <div>
                    <Badge className="mb-2 capitalize">{block.reason}</Badge>
                    <p className="text-[#1A1815] font-medium">{formatDateTime(block.start_time)} — {formatDateTime(block.end_time)}</p>
                    {block.staff_id && (
                      <p className="text-sm text-[#635E54]">
                        {staff.find(s => s.staff_id === block.staff_id)?.name || 'Profissional'}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEditBlock(block)} className="rounded-full">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteBlock(block.block_id)} className="rounded-full text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Design Tab - Colors & Images */}
          <TabsContent value="design" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="font-heading text-xl font-medium text-[#1A1815]">Design do Site</h2>
                <p className="text-sm text-[#635E54]">Personalize cores e imagens</p>
              </div>
              <Button onClick={handleSaveSettings} className="bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full">
                <Save className="w-4 h-4 mr-2" />Guardar Alterações
              </Button>
            </div>

            {siteSettings && (
              <div className="grid gap-6">
                {/* Colors */}
                <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                  <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4 flex items-center gap-2">
                    <Palette className="w-5 h-5 text-[#BFA05F]" /> Cores do Site
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <label className="text-sm text-[#635E54]">Cor Principal</label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={siteSettings.primary_color} onChange={(e) => setSiteSettings({ ...siteSettings, primary_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                        <Input value={siteSettings.primary_color} onChange={(e) => setSiteSettings({ ...siteSettings, primary_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Cor Secundária</label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={siteSettings.secondary_color} onChange={(e) => setSiteSettings({ ...siteSettings, secondary_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                        <Input value={siteSettings.secondary_color} onChange={(e) => setSiteSettings({ ...siteSettings, secondary_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Cor Destaque</label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={siteSettings.accent_color} onChange={(e) => setSiteSettings({ ...siteSettings, accent_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                        <Input value={siteSettings.accent_color} onChange={(e) => setSiteSettings({ ...siteSettings, accent_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Cor Fundo</label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={siteSettings.background_color} onChange={(e) => setSiteSettings({ ...siteSettings, background_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                        <Input value={siteSettings.background_color} onChange={(e) => setSiteSettings({ ...siteSettings, background_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Cor Texto</label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={siteSettings.text_color} onChange={(e) => setSiteSettings({ ...siteSettings, text_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                        <Input value={siteSettings.text_color} onChange={(e) => setSiteSettings({ ...siteSettings, text_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Button Colors */}
                <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                  <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4 flex items-center gap-2">
                    <Palette className="w-5 h-5 text-[#BFA05F]" /> Cores dos Botões
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-[#635E54]">Fundo do Botão</label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={siteSettings.button_bg_color || '#1A1815'} onChange={(e) => setSiteSettings({ ...siteSettings, button_bg_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                        <Input value={siteSettings.button_bg_color || '#1A1815'} onChange={(e) => setSiteSettings({ ...siteSettings, button_bg_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Texto do Botão</label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={siteSettings.button_text_color || '#FFFFFF'} onChange={(e) => setSiteSettings({ ...siteSettings, button_text_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                        <Input value={siteSettings.button_text_color || '#FFFFFF'} onChange={(e) => setSiteSettings({ ...siteSettings, button_text_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Fundo ao Passar (Hover)</label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={siteSettings.button_hover_bg_color || '#BFA05F'} onChange={(e) => setSiteSettings({ ...siteSettings, button_hover_bg_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                        <Input value={siteSettings.button_hover_bg_color || '#BFA05F'} onChange={(e) => setSiteSettings({ ...siteSettings, button_hover_bg_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Texto ao Passar (Hover)</label>
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={siteSettings.button_hover_text_color || '#FFFFFF'} onChange={(e) => setSiteSettings({ ...siteSettings, button_hover_text_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                        <Input value={siteSettings.button_hover_text_color || '#FFFFFF'} onChange={(e) => setSiteSettings({ ...siteSettings, button_hover_text_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div className="mt-6 p-4 bg-[#F9F8F6] rounded-xl">
                    <p className="text-sm text-[#635E54] mb-3">Pré-visualização:</p>
                    <div className="flex gap-4 flex-wrap">
                      <button 
                        style={{ 
                          backgroundColor: siteSettings.button_bg_color || '#1A1815',
                          color: siteSettings.button_text_color || '#FFFFFF'
                        }}
                        className="px-6 py-3 rounded-full font-medium transition-colors"
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = siteSettings.button_hover_bg_color || '#BFA05F';
                          e.target.style.color = siteSettings.button_hover_text_color || '#FFFFFF';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = siteSettings.button_bg_color || '#1A1815';
                          e.target.style.color = siteSettings.button_text_color || '#FFFFFF';
                        }}
                      >
                        Agendar Consulta
                      </button>
                      <button 
                        style={{ 
                          backgroundColor: siteSettings.button_bg_color || '#1A1815',
                          color: siteSettings.button_text_color || '#FFFFFF'
                        }}
                        className="px-6 py-3 rounded-full font-medium transition-colors"
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = siteSettings.button_hover_bg_color || '#BFA05F';
                          e.target.style.color = siteSettings.button_hover_text_color || '#FFFFFF';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = siteSettings.button_bg_color || '#1A1815';
                          e.target.style.color = siteSettings.button_text_color || '#FFFFFF';
                        }}
                      >
                        Ver Serviços
                      </button>
                    </div>
                  </div>
                </div>

                {/* Images - URLs Externas */}
                <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                  <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4 flex items-center gap-2">
                    <Image className="w-5 h-5 text-[#BFA05F]" /> Imagens do Site
                  </h3>
                  <p className="text-sm text-[#A8A29A] mb-4">Cole URLs de imagens externas (Cloudinary, Unsplash, etc.)</p>
                  <div className="grid grid-cols-1 gap-6">
                    {/* Hero Image */}
                    <div>
                      <label className="text-sm text-[#635E54]">Imagem Principal (Hero)</label>
                      <Input 
                        value={siteSettings.hero_image || ''} 
                        onChange={(e) => setSiteSettings({ ...siteSettings, hero_image: e.target.value })} 
                        placeholder="https://res.cloudinary.com/... ou https://images.unsplash.com/..." 
                        className="mt-1" 
                      />
                      {siteSettings.hero_image && (
                        <div className="mt-2 relative group">
                          <img 
                            src={siteSettings.hero_image} 
                            alt="Preview Hero" 
                            className="w-full h-32 object-cover rounded-xl"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>

                    {/* About Image */}
                    <div>
                      <label className="text-sm text-[#635E54]">Imagem Sobre Nós</label>
                      <Input 
                        value={siteSettings.about_image || ''} 
                        onChange={(e) => setSiteSettings({ ...siteSettings, about_image: e.target.value })} 
                        placeholder="https://res.cloudinary.com/... ou https://images.unsplash.com/..." 
                        className="mt-1" 
                      />
                      {siteSettings.about_image && (
                        <div className="mt-2">
                          <img 
                            src={siteSettings.about_image} 
                            alt="Preview About" 
                            className="w-full h-32 object-cover rounded-xl"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Culture Image */}
                    <div>
                      <label className="text-sm text-[#635E54]">Imagem Cultura</label>
                      <Input 
                        value={siteSettings.culture_image || ''} 
                        onChange={(e) => setSiteSettings({ ...siteSettings, culture_image: e.target.value })} 
                        placeholder="https://res.cloudinary.com/... ou https://images.unsplash.com/..." 
                        className="mt-1" 
                      />
                      {siteSettings.culture_image && (
                        <div className="mt-2">
                          <img 
                            src={siteSettings.culture_image} 
                            alt="Preview Culture" 
                            className="w-full h-32 object-cover rounded-xl"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Mission Image */}
                    <div>
                      <label className="text-sm text-[#635E54]">Imagem Missão</label>
                      <Input 
                        value={siteSettings.mission_image || ''} 
                        onChange={(e) => setSiteSettings({ ...siteSettings, mission_image: e.target.value })} 
                        placeholder="https://res.cloudinary.com/... ou https://images.unsplash.com/..." 
                        className="mt-1" 
                      />
                      {siteSettings.mission_image && (
                        <div className="mt-2">
                          <img 
                            src={siteSettings.mission_image} 
                            alt="Preview Mission" 
                            className="w-full h-32 object-cover rounded-xl"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Brand */}
                <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                  <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4">Marca</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#635E54]">Nome do Estúdio</label>
                      <Input value={siteSettings.brand_name} onChange={(e) => setSiteSettings({ ...siteSettings, brand_name: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Logo URL (opcional)</label>
                      <Input 
                        value={siteSettings.brand_logo || ''} 
                        onChange={(e) => setSiteSettings({ ...siteSettings, brand_logo: e.target.value })} 
                        placeholder="https://res.cloudinary.com/..." 
                        className="mt-1" 
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Content Tab - Texts */}
          <TabsContent value="content" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="font-heading text-xl font-medium text-[#1A1815]">Textos e Conteúdo</h2>
                <p className="text-sm text-[#635E54]">Edite os textos do site</p>
              </div>
              <Button onClick={handleSaveSettings} className="bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full">
                <Save className="w-4 h-4 mr-2" />Guardar Alterações
              </Button>
            </div>

            {siteSettings && (
              <div className="grid gap-6">
                {/* Hero Section */}
                <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                  <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4">Secção Principal (Hero)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#635E54]">Título (PT)</label>
                      <Input value={siteSettings.hero_title_pt} onChange={(e) => setSiteSettings({ ...siteSettings, hero_title_pt: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Title (EN)</label>
                      <Input value={siteSettings.hero_title_en} onChange={(e) => setSiteSettings({ ...siteSettings, hero_title_en: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Título Destaque (PT)</label>
                      <Input value={siteSettings.hero_title_accent_pt} onChange={(e) => setSiteSettings({ ...siteSettings, hero_title_accent_pt: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Title Accent (EN)</label>
                      <Input value={siteSettings.hero_title_accent_en} onChange={(e) => setSiteSettings({ ...siteSettings, hero_title_accent_en: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Subtítulo (PT)</label>
                      <Textarea value={siteSettings.hero_subtitle_pt} onChange={(e) => setSiteSettings({ ...siteSettings, hero_subtitle_pt: e.target.value })} className="mt-1" rows={2} />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Subtitle (EN)</label>
                      <Textarea value={siteSettings.hero_subtitle_en} onChange={(e) => setSiteSettings({ ...siteSettings, hero_subtitle_en: e.target.value })} className="mt-1" rows={2} />
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                  <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4">Texto dos Botões</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#635E54]">Botão Agendar (PT)</label>
                      <Input value={siteSettings.btn_book_pt} onChange={(e) => setSiteSettings({ ...siteSettings, btn_book_pt: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Book Button (EN)</label>
                      <Input value={siteSettings.btn_book_en} onChange={(e) => setSiteSettings({ ...siteSettings, btn_book_en: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Botão Descobrir (PT)</label>
                      <Input value={siteSettings.btn_discover_pt} onChange={(e) => setSiteSettings({ ...siteSettings, btn_discover_pt: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Discover Button (EN)</label>
                      <Input value={siteSettings.btn_discover_en} onChange={(e) => setSiteSettings({ ...siteSettings, btn_discover_en: e.target.value })} className="mt-1" />
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                  <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4">Informações de Contacto</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="text-sm text-[#635E54]">Morada</label>
                      <Input value={siteSettings.address} onChange={(e) => setSiteSettings({ ...siteSettings, address: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Telefone</label>
                      <Input value={siteSettings.phone} onChange={(e) => setSiteSettings({ ...siteSettings, phone: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Email</label>
                      <Input value={siteSettings.email} onChange={(e) => setSiteSettings({ ...siteSettings, email: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Horário (PT)</label>
                      <Input value={siteSettings.business_hours_pt} onChange={(e) => setSiteSettings({ ...siteSettings, business_hours_pt: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Hours (EN)</label>
                      <Input value={siteSettings.business_hours_en} onChange={(e) => setSiteSettings({ ...siteSettings, business_hours_en: e.target.value })} className="mt-1" />
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                  <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4">Redes Sociais</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#635E54]">Instagram</label>
                      <Input value={siteSettings.instagram} onChange={(e) => setSiteSettings({ ...siteSettings, instagram: e.target.value })} className="mt-1" placeholder="https://instagram.com/..." />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Facebook</label>
                      <Input value={siteSettings.facebook} onChange={(e) => setSiteSettings({ ...siteSettings, facebook: e.target.value })} className="mt-1" placeholder="https://facebook.com/..." />
                    </div>
                  </div>
                </div>

                {/* About */}
                <div className="bg-white rounded-2xl p-6 border border-[#E6E2DA]">
                  <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4">Página Sobre Nós</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#635E54]">Título (PT)</label>
                      <Input value={siteSettings.about_title_pt} onChange={(e) => setSiteSettings({ ...siteSettings, about_title_pt: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Title (EN)</label>
                      <Input value={siteSettings.about_title_en} onChange={(e) => setSiteSettings({ ...siteSettings, about_title_en: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Texto (PT)</label>
                      <Textarea value={siteSettings.about_text_pt} onChange={(e) => setSiteSettings({ ...siteSettings, about_text_pt: e.target.value })} className="mt-1" rows={4} />
                    </div>
                    <div>
                      <label className="text-sm text-[#635E54]">Text (EN)</label>
                      <Textarea value={siteSettings.about_text_en} onChange={(e) => setSiteSettings({ ...siteSettings, about_text_en: e.target.value })} className="mt-1" rows={4} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
